// src/components/GiornaleVistaMensile.js
// Vista a matrice: righe = giorni del mese, colonne = dipendenti, celle = ore lavorate.
// Weekend evidenziati in rosso, coerente con i giornali dei lavori cartacei tradizionali.
import React, { useState, useMemo, useEffect } from "react";
import { collection, doc, addDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { confirm } from "../utils/confirm";
import { messaggioErrore } from "../utils/erroriUtente";
import { ChevronLeft, ChevronRight, Printer, GripVertical } from "lucide-react";

const MESI = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const GIORNI_SETT = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];

function pad2(n){ return String(n).padStart(2,"0"); }
function dataStr(d){ return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`; }

const METEO_BREVE = {
  "Sole":"Sole", "Parzialmente nuvoloso":"P. Nuv.", "Nuvoloso":"Nuvoloso",
  "Pioggia":"Pioggia", "Temporale":"Temporale", "Neve":"Neve",
  "Vento forte":"Vento", "Nebbia":"Nebbia",
};
function meteoBreve(m) {
  if (!m) return "";
  const pulito = m.replace(/[☀️☁️⛅🌧️⛈️🌨️💨🌫️]/g,"").trim();
  return METEO_BREVE[pulito] || pulito;
}

export default function GiornaleVistaMensile({ projectId, orgId, project, datiCommessa, orgInfo, giorni, dipendenti, currentUser, addToast, onApriGiorno }) {
  const oggi = new Date();
  const [meseVisto, setMeseVisto] = useState(new Date(oggi.getFullYear(), oggi.getMonth(), 1));
  const [celleModifica, setCelleModifica] = useState({}); // "data|dipId" -> valore in editing
  const [giornoTrascinato, setGiornoTrascinato] = useState(null); // Date del giorno sorgente durante il trascinamento
  const [giornoSuCuiSiPassa, setGiornoSuCuiSiPassa] = useState(null); // Date del giorno sotto il cursore, per l'evidenziazione
  const [celllaTrascinata, setCellaTrascinata] = useState(null); // { giorno, dip } della singola cella durante il trascinamento
  const [cellaSuCuiSiPassa, setCellaSuCuiSiPassa] = useState(null); // "data|dipId" della cella sotto il cursore

  const numGiorni = new Date(meseVisto.getFullYear(), meseVisto.getMonth()+1, 0).getDate();
  const righeGiorni = Array.from({length:numGiorni}, (_,i) => new Date(meseVisto.getFullYear(), meseVisto.getMonth(), i+1));

  const giornoDoc = (d) => giorni.find(g => g.data === dataStr(d));

  // Colonne dinamiche: solo gli operai/figure professionali realmente compilati nei giorni di questo mese,
  // in ordine di prima comparsa nel mese così l'ordine resta stabile mentre digiti.
  const colonne = useMemo(() => {
    const mappa = new Map();
    righeGiorni.forEach(d => {
      const gd = giornoDoc(d);
      (gd?.manodopera||[]).forEach(r => {
        const key = r.dipendenteId || r.nome;
        if (!key || !r.nome) return;
        if (!mappa.has(key)) {
          const dipReale = dipendenti.find(x=>x.id===r.dipendenteId);
          mappa.set(key, { id: r.dipendenteId || key, nome: dipReale?.nome || r.nome, cognome: dipReale?.cognome || "" });
        }
      });
    });
    return Array.from(mappa.values());
  }, [giorni, meseVisto, dipendenti]);

  // Trascinamento gestito a mano col mouse (più affidabile del drag-and-drop nativo su una tabella,
  // che su alcuni browser semplicemente non funziona all'interno di <tr>/<td>).
  useEffect(() => {
    if (!giornoTrascinato) return;
    let ultimoIndiceProcessato = righeGiorni.findIndex(d => dataStr(d) === dataStr(giornoTrascinato));
    const trovaGiornoSottoCursore = (clientX, clientY) => {
      const el = document.elementFromPoint(clientX, clientY);
      const riga = el && el.closest ? el.closest("tr[data-giorno]") : null;
      if (!riga) return null;
      const ds = riga.getAttribute("data-giorno");
      const idx = righeGiorni.findIndex(d => dataStr(d) === ds);
      return idx === -1 ? null : { ds, idx };
    };
    document.body.style.cursor = "grabbing";
    const onMove = (e) => {
      const g = trovaGiornoSottoCursore(e.clientX, e.clientY);
      setGiornoSuCuiSiPassa(g ? new Date(g.ds+"T00:00:00") : null);
      if (g && g.idx !== ultimoIndiceProcessato) {
        const passi = Math.abs(g.idx - ultimoIndiceProcessato);
        const direzione = g.idx > ultimoIndiceProcessato ? 1 : -1;
        for (let i = 1; i <= passi; i++) {
          const giornoTarget = righeGiorni[ultimoIndiceProcessato + i*direzione];
          if (giornoTarget) copiaManodopera(giornoTrascinato, giornoTarget, true);
        }
        ultimoIndiceProcessato = g.idx;
      }
    };
    const onUp = () => {
      setGiornoTrascinato(null);
      setGiornoSuCuiSiPassa(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [giornoTrascinato]);

  // Trascinamento della singola cella (un operaio, un giorno) — resta sempre nella stessa colonna,
  // così sposti solo il valore di quella persona senza toccare gli altri.
  useEffect(() => {
    if (!celllaTrascinata) return;
    const rowIdxIniziale = righeGiorni.findIndex(d => dataStr(d) === dataStr(celllaTrascinata.giorno));
    const colIdxIniziale = colonne.findIndex(c => c.id === celllaTrascinata.dip.id);
    let ultimaRiga = rowIdxIniziale, ultimaCol = colIdxIniziale;

    const trovaCellaSottoCursore = (clientX, clientY) => {
      const el = document.elementFromPoint(clientX, clientY);
      const cella = el && el.closest ? el.closest("td[data-dip]") : null;
      if (!cella) return null;
      const dipId = cella.getAttribute("data-dip");
      const giornoStr = cella.getAttribute("data-giorno");
      const colIdx = colonne.findIndex(c => c.id === dipId);
      const rowIdx = righeGiorni.findIndex(d => dataStr(d) === giornoStr);
      if (colIdx === -1 || rowIdx === -1) return null;
      return { rowIdx, colIdx, giorno: giornoStr, dip: colonne[colIdx] };
    };

    // Applica il valore a ogni cella lungo il percorso da (r1,c1) a (r2,c2), per non saltare
    // quelle intermedie quando il mouse si muove velocemente (es. trascinamento orizzontale veloce).
    const applicaLungoPercorso = (r1, c1, r2, c2) => {
      const passi = Math.max(Math.abs(r2 - r1), Math.abs(c2 - c1));
      for (let i = 1; i <= passi; i++) {
        const r = Math.round(r1 + ((r2 - r1) * i) / passi);
        const c = Math.round(c1 + ((c2 - c1) * i) / passi);
        const giornoTarget = righeGiorni[r];
        const dipTarget = colonne[c];
        if (giornoTarget && dipTarget) salvaOre(giornoTarget, dipTarget, celllaTrascinata.valore);
      }
    };

    document.body.style.cursor = "grabbing";
    const onMove = (e) => {
      const c = trovaCellaSottoCursore(e.clientX, e.clientY);
      setCellaSuCuiSiPassa(c ? `${c.giorno}|${c.dip.id}` : null);
      if (c && (c.rowIdx !== ultimaRiga || c.colIdx !== ultimaCol)) {
        applicaLungoPercorso(ultimaRiga, ultimaCol, c.rowIdx, c.colIdx);
        ultimaRiga = c.rowIdx;
        ultimaCol = c.colIdx;
      }
    };
    const onUp = () => {
      setCellaTrascinata(null);
      setCellaSuCuiSiPassa(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [celllaTrascinata, colonne]);



  const oreDi = (giornoData, dipId, dipNome) => {
    const gd = giornoDoc(giornoData);
    if (!gd) return "";
    const riga = (gd.manodopera||[]).find(r => (dipId && r.dipendenteId===dipId) || (!dipId && r.nome===dipNome));
    return riga?.ore ?? "";
  };

  const totalePerDipendente = (dip) => righeGiorni.reduce((s,d) =>
    s + (parseFloat(oreDi(d, dip.id, `${dip.nome||""} ${dip.cognome||""}`.trim())) || 0), 0);
  const totaleGenerale = colonne.reduce((s,dip) => s + totalePerDipendente(dip), 0);

  const salvaOre = async (giornoData, dip, valore) => {
    const ds = dataStr(giornoData);
    const gd = giornoDoc(giornoData);
    const nomeDip = `${dip.nome||""} ${dip.cognome||""}`.trim() || dip.displayName || "";
    try {
      if (gd) {
        const attuale = [...(gd.manodopera||[])];
        const idx = attuale.findIndex(r => (dip.id && r.dipendenteId===dip.id) || (!dip.id && r.nome===nomeDip));
        if (valore === "") {
          if (idx>=0) attuale.splice(idx,1);
        } else if (idx>=0) {
          attuale[idx] = { ...attuale[idx], ore: valore, dipendenteId: dip.id||"", nome: nomeDip };
        } else {
          attuale.push({ id: Date.now().toString()+Math.random(), dipendenteId: dip.id||"", nome: nomeDip, ore: valore });
        }
        await updateDoc(doc(db,"giornaleCantiereDays",gd.id), { manodopera: attuale, updatedAt: serverTimestamp() });
      } else if (valore !== "") {
        await addDoc(collection(db,"giornaleCantiereDays"), {
          projectId, orgId, data: ds,
          manodopera: [{ id: Date.now().toString()+Math.random(), dipendenteId: dip.id||"", nome: nomeDip, ore: valore }],
          responsabile: currentUser?.displayName||"", createdAt: serverTimestamp(),
        });
      }
    } catch(err) {
      addToast && addToast(messaggioErrore(err, "salvare le ore"), "error");
    }
  };

  const copiaManodopera = async (giornoSorgente, giornoDestinazione, saltaConferma=false) => {
    if (dataStr(giornoSorgente) === dataStr(giornoDestinazione)) return;
    const gdSorgente = giornoDoc(giornoSorgente);
    const manodoperaSorgente = gdSorgente?.manodopera || [];
    if (manodoperaSorgente.length === 0) {
      addToast && addToast("Il giorno di partenza non ha manodopera da copiare", "error");
      return;
    }
    const gdDestinazione = giornoDoc(giornoDestinazione);
    if (!saltaConferma && (gdDestinazione?.manodopera || []).length > 0) {
      const dsDest = dataStr(giornoDestinazione);
      if (!(await confirm(`Il giorno ${dsDest.split("-").reverse().join("/")} ha già manodopera inserita. Sovrascriverla con quella copiata?`, { type: "danger" }))) return;
    }
    const copia = manodoperaSorgente.map(r => ({ ...r }));
    try {
      if (gdDestinazione) {
        await updateDoc(doc(db, "giornaleCantiereDays", gdDestinazione.id), { manodopera: copia, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, "giornaleCantiereDays"), {
          projectId, orgId, data: dataStr(giornoDestinazione),
          manodopera: copia, responsabile: currentUser?.displayName || "", createdAt: serverTimestamp(),
        });
      }
      addToast && addToast("Manodopera copiata ✅", "success");
    } catch (err) {
      addToast && addToast(messaggioErrore(err, "copiare la manodopera"), "error");
    }
  };

  const stampaPDF = () => {
    const win = window.open("","_blank");
    const logoHtml = orgInfo?.logo ? `<img src="${orgInfo.logo}" style="height:32px;object-fit:contain;"/>` :
      `<div style="font-size:14px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;

    const righeHtml = righeGiorni.map(d => {
      const isWeekend = d.getDay()===0 || d.getDay()===6;
      const gd = giornoDoc(d);
      const oreCols = colonne.map(dip => `<td style="text-align:center;border:1px solid #ccc;padding:3px;">${oreDi(d, dip.id, `${dip.nome||""} ${dip.cognome||""}`.trim())||""}</td>`).join("");
      const critHtml = (gd?.criticita||[]).map(c=>{
        const statoLbl = c.stato==="risolta"?"Risolta":c.stato==="in_corso"?"In corso":"Aperta";
        return `<div style="margin-bottom:2px;"><strong>[${statoLbl}]</strong> ${c.descrizione||""}${c.propostaSoluzione?` — Soluzione: ${c.propostaSoluzione}`:""}${c.esito?` — Esito: ${c.esito}`:""}</div>`;
      }).join("");
      return `<tr style="${isWeekend?"background:#fee2e2;":""}">
        <td style="border:1px solid #ccc;padding:3px 6px;font-weight:${isWeekend?"700":"400"};color:${isWeekend?"#dc2626":"#111"};white-space:nowrap;">${pad2(d.getDate())}/${pad2(d.getMonth()+1)}/${d.getFullYear()}</td>
        <td style="border:1px solid #ccc;padding:3px 6px;white-space:nowrap;">${meteoBreve(gd?.meteo)}</td>
        <td style="border:1px solid #ccc;padding:3px 6px;">${gd?.lavorieseguiti||""}</td>
        ${oreCols}
        <td style="border:1px solid #ccc;padding:3px 6px;">${gd?.materialiRicevuti||""}</td>
        <td style="border:1px solid #ccc;padding:3px 6px;">${gd?.annotazioni||""}</td>
        <td style="border:1px solid #ccc;padding:3px 6px;">${gd?.visiteIspettive||""}</td>
        <td style="border:1px solid #ccc;padding:3px 6px;">${critHtml}</td>
      </tr>`;
    }).join("");

    const intestazioniOperai = colonne.map(dip =>
      `<th style="border:1px solid #ccc;padding:3px;writing-mode:vertical-rl;text-orientation:mixed;font-size:9px;min-width:20px;">${dip.nome} ${(dip.cognome||"")[0]||""}.</th>`
    ).join("");

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Giornale dei Lavori — ${MESI[meseVisto.getMonth()]} ${meseVisto.getFullYear()}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;color:#111;padding:6mm 8mm;font-size:9px;}
      @media screen{body{max-width:1150px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 landscape;margin:6mm;}
      table{width:100%;border-collapse:collapse;}
      .hdr{border-bottom:2px solid #1e1b4b;padding-bottom:6px;margin-bottom:8px;display:flex;justify-content:space-between;align-items:center;}
    </style></head><body>
    <div class="hdr">
      <div>${logoHtml}</div>
      <div style="text-align:right">
        <div style="font-size:13px;font-weight:800;color:#1e1b4b">GIORNALE DEI LAVORI — ${MESI[meseVisto.getMonth()].toUpperCase()} ${meseVisto.getFullYear()}</div>
        <div style="font-size:10px;color:#6b7280">${project?.codiceCommessa?`[${project.codiceCommessa}] `:""}${project?.name||""}</div>
      </div>
    </div>
    <div style="font-size:9px;margin-bottom:8px;line-height:1.5;">
      <div><strong>Committente:</strong> ${datiCommessa?.appaltante_nome || project?.clienteNome || "—"} &nbsp;·&nbsp; <strong>Lavori:</strong> ${project?.name||""}${project?.description?` — ${project.description}`:""}</div>
      <div>
        <strong>Impresa esecutrice:</strong> ${datiCommessa?.appaltatore_nome||"—"} &nbsp;·&nbsp;
        <strong>Impresa subappaltatrice:</strong> ${datiCommessa?.subappaltatrice_nome||"—"} &nbsp;·&nbsp;
        <strong>Direttore dei Lavori:</strong> ${datiCommessa?.dl_nome||"—"}
        ${datiCommessa?.progetto_esecutivo_nome?` &nbsp;·&nbsp; <strong>Progetto Esecutivo:</strong> ${datiCommessa.progetto_esecutivo_nome}`:""}
        ${datiCommessa?.cse_nome?` &nbsp;·&nbsp; <strong>CSE:</strong> ${datiCommessa.cse_nome}`:""}
        ${datiCommessa?.rup_nome?` &nbsp;·&nbsp; <strong>RUP:</strong> ${datiCommessa.rup_nome}`:""}
      </div>
    </div>
    <table>
      <thead>
        <tr>
          <th style="border:1px solid #ccc;padding:3px;">Data</th>
          <th style="border:1px solid #ccc;padding:3px;min-width:60px;">Meteo</th>
          <th style="border:1px solid #ccc;padding:3px;min-width:110px;">Annotazioni / Lavori eseguiti</th>
          ${intestazioniOperai}
          <th style="border:1px solid #ccc;padding:3px;min-width:80px;">Materiali</th>
          <th style="border:1px solid #ccc;padding:3px;min-width:80px;">Note</th>
          <th style="border:1px solid #ccc;padding:3px;min-width:100px;">Osservazioni DL/RUP/CSE/Collaudatore</th>
          <th style="border:1px solid #ccc;padding:3px;min-width:130px;">Criticità e soluzioni</th>
        </tr>
      </thead>
      <tbody>${righeHtml}</tbody>
      <tfoot>
        <tr style="font-weight:700;background:#f3f4f6;">
          <td style="border:1px solid #ccc;padding:3px 6px;" colspan="2">Totale ore</td>
          <td style="border:1px solid #ccc;padding:3px 6px;"></td>
          ${colonne.map(dip=>`<td style="text-align:center;border:1px solid #ccc;padding:3px;">${totalePerDipendente(dip)||""}</td>`).join("")}
          <td style="border:1px solid #ccc;padding:3px;" colspan="3"></td>
          <td style="text-align:center;border:1px solid #ccc;padding:3px;color:#059669;">Totale generale: ${totaleGenerale}</td>
        </tr>
      </tfoot>
    </table>
    
    </body></html>`);
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div style={{ padding:"0 0 20px" }}>
      {/* Intestazione anagrafica — stessa struttura dei giornali dei lavori tradizionali */}
      <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:16, marginBottom:16, fontSize:12, lineHeight:1.6 }}>
        <div>
          <strong>Committente:</strong> {datiCommessa?.appaltante_nome || project?.clienteNome || "—"}
          {"  ·  "}<strong>Lavori:</strong> {project?.name || "—"}{project?.description ? ` — ${project.description}` : ""}
        </div>
        <div>
          <strong>Impresa esecutrice:</strong> {datiCommessa?.appaltatore_nome || "—"}
          {"  ·  "}<strong>Impresa subappaltatrice:</strong> {datiCommessa?.subappaltatrice_nome || "—"}
          {"  ·  "}<strong>Direttore dei Lavori:</strong> {datiCommessa?.dl_nome || "—"}
          {datiCommessa?.progetto_esecutivo_nome && <>{"  ·  "}<strong>Progetto Esecutivo:</strong> {datiCommessa.progetto_esecutivo_nome}</>}
          {datiCommessa?.cse_nome && <>{"  ·  "}<strong>CSE:</strong> {datiCommessa.cse_nome}</>}
          {datiCommessa?.rup_nome && <>{"  ·  "}<strong>RUP:</strong> {datiCommessa.rup_nome}</>}
        </div>
      </div>

      {/* Navigazione mese */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:16, marginBottom:12, position:"relative" }}>
        <button onClick={()=>setMeseVisto(m=>new Date(m.getFullYear(), m.getMonth()-1, 1))}
          style={{ background:"none", border:"1px solid #e5e7eb", borderRadius:8, padding:6, cursor:"pointer" }}><ChevronLeft size={16}/></button>
        <span style={{ fontSize:14, fontWeight:700, minWidth:160, textAlign:"center" }}>{MESI[meseVisto.getMonth()]} {meseVisto.getFullYear()}</span>
        <button onClick={()=>setMeseVisto(m=>new Date(m.getFullYear(), m.getMonth()+1, 1))}
          style={{ background:"none", border:"1px solid #e5e7eb", borderRadius:8, padding:6, cursor:"pointer" }}><ChevronRight size={16}/></button>
        <button onClick={stampaPDF}
          style={{ position:"absolute", right:0, display:"flex", alignItems:"center", gap:6, padding:"6px 14px", border:"1px solid #2563eb", borderRadius:8, background:"#dbeafe", color:"#1d4ed8", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
          <Printer size={13}/> Stampa PDF
        </button>
      </div>

      {colonne.length===0 ? (
        <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:13 }}>
          Nessuna manodopera ancora registrata per {MESI[meseVisto.getMonth()]} {meseVisto.getFullYear()}.<br/>
          <span style={{fontSize:12}}>Compila la tabella Manodopera in "📋 Dettaglio giorno" per far comparire qui le colonne.</span>
        </div>
      ) : (
        <div style={{ overflowX:"auto", border:"1px solid #e5e7eb", borderRadius:10 }}>
          <table style={{ borderCollapse:"collapse", fontSize:12, width:"100%" }}>
            <thead>
              <tr>
                <th style={{ position:"sticky", left:0, background:"#f9fafb", padding:"8px 10px", borderBottom:"2px solid #e5e7eb", borderRight:"1px solid #e5e7eb", minWidth:130, textAlign:"left", zIndex:2 }}>Data</th>
                <th style={{ position:"sticky", left:130, background:"#f9fafb", padding:"8px 10px", borderBottom:"2px solid #e5e7eb", borderRight:"2px solid #e5e7eb", minWidth:70, textAlign:"left", zIndex:2 }}>Meteo</th>
                <th style={{ position:"sticky", left:200, background:"#f9fafb", padding:"8px 10px", borderBottom:"2px solid #e5e7eb", borderRight:"2px solid #e5e7eb", minWidth:200, textAlign:"left", zIndex:2 }}>
                  Annotazioni / Lavori eseguiti
                </th>
                {colonne.map(dip => (
                  <th key={dip.id} style={{ padding:"6px 4px", borderBottom:"2px solid #e5e7eb", borderRight:"1px solid #f3f4f6", writingMode:"vertical-rl", textOrientation:"mixed", fontSize:11, fontWeight:700, color:"#374151", minWidth:34, maxHeight:110 }}>
                    {dip.nome} {(dip.cognome||"")[0]||""}.
                  </th>
                ))}
                <th style={{ padding:"8px 10px", borderBottom:"2px solid #e5e7eb", borderRight:"1px solid #f3f4f6", minWidth:160, textAlign:"left" }}>Materiali utilizzati</th>
                <th style={{ padding:"8px 10px", borderBottom:"2px solid #e5e7eb", borderRight:"1px solid #f3f4f6", minWidth:160, textAlign:"left" }}>Note</th>
                <th style={{ padding:"8px 10px", borderBottom:"2px solid #e5e7eb", borderRight:"1px solid #f3f4f6", minWidth:220, textAlign:"left" }}>
                  Osservazioni e istruzioni della direzione lavori, del responsabile del procedimento, del coordinatore per l'esecuzione, del collaudatore
                </th>
                <th style={{ padding:"8px 10px", borderBottom:"2px solid #e5e7eb", minWidth:240, textAlign:"left" }}>Criticità e soluzioni</th>
              </tr>
            </thead>
            <tbody>
              {righeGiorni.map((d, rowIdx) => {
                const isWeekend = d.getDay()===0 || d.getDay()===6;
                const gd = giornoDoc(d);
                const inEvidenza = giornoSuCuiSiPassa && dataStr(giornoSuCuiSiPassa)===dataStr(d);
                return (
                  <tr key={dataStr(d)} data-giorno={dataStr(d)}
                    style={{ background: inEvidenza ? "#ede9fe" : (isWeekend ? "#fef2f2" : "#fff"), outline: inEvidenza ? "2px dashed #7c3aed" : "none", outlineOffset:-2 }}>
                    <td onClick={()=>onApriGiorno && onApriGiorno(d)}
                      style={{ position:"sticky", left:0, background: inEvidenza ? "#ede9fe" : (isWeekend ? "#fef2f2" : "#fff"), padding:"6px 6px 6px 4px", borderBottom:"1px solid #f3f4f6", borderRight:"1px solid #e5e7eb", cursor:"pointer", color: isWeekend?"#dc2626":"#111827", fontWeight: isWeekend?700:500 }}>
                      <span
                        onMouseDown={e=>{ if((gd?.manodopera||[]).length===0) return; e.preventDefault(); e.stopPropagation(); setGiornoTrascinato(d); }}
                        title={(gd?.manodopera||[]).length>0 ? "Trascina su un altro giorno per copiare la manodopera" : ""}
                        style={{ cursor:(gd?.manodopera||[]).length>0?"grab":"default", display:"inline-flex", verticalAlign:"middle", marginRight:4, color:(gd?.manodopera||[]).length>0?"#7c3aed":"#e5e7eb" }}>
                        <GripVertical size={12}/>
                      </span>
                      {pad2(d.getDate())}/{pad2(d.getMonth()+1)} · {GIORNI_SETT[d.getDay()]}
                    </td>
                    <td onClick={()=>onApriGiorno && onApriGiorno(d)}
                      style={{ position:"sticky", left:130, background: isWeekend ? "#fef2f2" : "#fff", padding:"6px 8px", borderBottom:"1px solid #f3f4f6", borderRight:"2px solid #e5e7eb", cursor:"pointer", fontSize:11, color:"#6b7280", whiteSpace:"nowrap" }}>
                      {meteoBreve(gd?.meteo)}
                    </td>
                    <td onClick={()=>onApriGiorno && onApriGiorno(d)}
                      style={{ position:"sticky", left:200, background: isWeekend ? "#fef2f2" : "#fff", padding:"6px 10px", borderBottom:"1px solid #f3f4f6", borderRight:"2px solid #e5e7eb", cursor:"pointer", color:"#6b7280", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:220 }}>
                      {gd?.lavorieseguiti || (isWeekend ? "" : "")}
                    </td>
                    {colonne.map((dip, colIdx) => {
                      const chiave = `${dataStr(d)}|${dip.id}`;
                      const valore = celleModifica[chiave] !== undefined ? celleModifica[chiave] : oreDi(d, dip.id, `${dip.nome||""} ${dip.cognome||""}`.trim());
                      const inEvidenzaCella = cellaSuCuiSiPassa === chiave;
                      const spostaFoco = (r,c) => {
                        const target = document.getElementById(`cellinput-${r}-${c}`);
                        if (target) { target.focus(); target.select && target.select(); }
                      };
                      return (
                        <td key={dip.id} data-giorno={dataStr(d)} data-dip={dip.id}
                          style={{ position:"relative", padding:0, borderBottom:"1px solid #f3f4f6", borderRight:"1px solid #f3f4f6", textAlign:"center", background: inEvidenzaCella?"#ede9fe":"transparent", outline: inEvidenzaCella?"2px dashed #7c3aed":"none", outlineOffset:-2 }}>
                          <input id={`cellinput-${rowIdx}-${colIdx}`} value={valore} inputMode="decimal"
                            onChange={e=>setCelleModifica(c=>({...c,[chiave]:e.target.value}))}
                            onBlur={e=>{
                              const v = e.target.value.trim();
                              salvaOre(d, dip, v);
                              setCelleModifica(c=>{ const n={...c}; delete n[chiave]; return n; });
                            }}
                            onKeyDown={e=>{
                              if (e.key==="ArrowUp") { e.preventDefault(); spostaFoco(rowIdx-1, colIdx); }
                              else if (e.key==="ArrowDown") { e.preventDefault(); spostaFoco(rowIdx+1, colIdx); }
                              else if (e.key==="ArrowLeft" && e.target.selectionStart===0) { e.preventDefault(); spostaFoco(rowIdx, colIdx-1); }
                              else if (e.key==="ArrowRight" && e.target.selectionStart===e.target.value.length) { e.preventDefault(); spostaFoco(rowIdx, colIdx+1); }
                            }}
                            style={{ width:34, height:30, border:"none", textAlign:"center", fontSize:12, background:"transparent", outline:"none" }}/>
                          {valore!=="" && (
                            <span onMouseDown={e=>{ e.preventDefault(); e.stopPropagation(); setCellaTrascinata({giorno:d, dip, valore}); }}
                              title="Trascina per copiare questo valore su più giorni"
                              style={{ position:"absolute", right:0, bottom:0, width:10, height:10, background:"transparent", cursor:"grab" }}/>
                          )}
                        </td>
                      );
                    })}
                    <td onClick={()=>onApriGiorno && onApriGiorno(d)}
                      style={{ padding:"6px 10px", borderBottom:"1px solid #f3f4f6", borderRight:"1px solid #f3f4f6", cursor:"pointer", color:"#6b7280", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:160 }}>
                      {gd?.materialiRicevuti || ""}
                    </td>
                    <td onClick={()=>onApriGiorno && onApriGiorno(d)}
                      style={{ padding:"6px 10px", borderBottom:"1px solid #f3f4f6", borderRight:"1px solid #f3f4f6", cursor:"pointer", color:"#6b7280", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:160 }}>
                      {gd?.annotazioni || ""}
                    </td>
                    <td onClick={()=>onApriGiorno && onApriGiorno(d)}
                      style={{ padding:"6px 10px", borderBottom:"1px solid #f3f4f6", borderRight:"1px solid #f3f4f6", cursor:"pointer", color:"#6b7280", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:220 }}>
                      {gd?.visiteIspettive || ""}
                    </td>
                    <td onClick={()=>onApriGiorno && onApriGiorno(d)}
                      style={{ padding:"6px 10px", borderBottom:"1px solid #f3f4f6", cursor:"pointer", color:"#92400e", fontSize:11, maxWidth:240 }}>
                      {(gd?.criticita||[]).map(c=>{
                        const badge = c.stato==="risolta"?"✅":c.stato==="in_corso"?"🔵":"🔴";
                        return `${badge} ${c.descrizione||""}`;
                      }).join(" · ")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background:"#f3f4f6", fontWeight:700 }}>
                <td style={{ position:"sticky", left:0, background:"#f3f4f6", padding:"8px 10px", borderTop:"2px solid #e5e7eb", borderRight:"1px solid #e5e7eb" }}>Totale ore</td>
                <td style={{ position:"sticky", left:130, background:"#f3f4f6", borderTop:"2px solid #e5e7eb", borderRight:"2px solid #e5e7eb" }}></td>
                <td style={{ position:"sticky", left:200, background:"#f3f4f6", borderTop:"2px solid #e5e7eb", borderRight:"2px solid #e5e7eb" }}></td>
                {colonne.map(dip => (
                  <td key={dip.id} style={{ textAlign:"center", padding:"6px 4px", borderTop:"2px solid #e5e7eb", borderRight:"1px solid #e5e7eb", fontSize:12, color:"#7c3aed" }}>
                    {totalePerDipendente(dip) || ""}
                  </td>
                ))}
                <td style={{ borderTop:"2px solid #e5e7eb", borderRight:"1px solid #e5e7eb" }}></td>
                <td style={{ borderTop:"2px solid #e5e7eb", borderRight:"1px solid #e5e7eb" }}></td>
                <td style={{ borderTop:"2px solid #e5e7eb", borderRight:"1px solid #e5e7eb" }}></td>
                <td style={{ textAlign:"center", padding:"6px 10px", borderTop:"2px solid #e5e7eb", color:"#059669", fontSize:13 }}>Tot: {totaleGenerale}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <p style={{ fontSize:11, color:"#9ca3af", marginTop:10 }}>
        Clicca su una data per aprire il giornale completo di quel giorno (meteo, lavori eseguiti, criticità, firme). Le ore inserite qui si sincronizzano automaticamente con la tabella Manodopera di quel giorno, e viceversa.
      </p>
    </div>
  );
}
